import { useMemo, useState, type FormEvent } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Button,
  CurrencyField,
  FormActions,
  FormGrid,
  NumberField,
  TextField,
  TextareaField,
} from "@atlas/ui/web";
import { CET_MINIMO_AM, avisoCoerenciaParcela, cetAnual } from "@atlas/domain";
import { atlas } from "../../../lib/sdk";
import type { NovoContratoBody } from "@atlas/sdk";
import { bancoPermissaoCobre, readBancoPermissoesDaSessao } from "../../../lib/banco-perms";

type Modo = "averbar" | "reservar";
// PORTABILIDADE saiu da ficha (02/10/2026): tem rito proprio em /banco/portabilidade.
type Tipo = "EMPRESTIMO" | "REFIN" | "COMPOSTA";

interface Props {
  modo: Modo;
  tipo: Tipo;
}

const TITULOS: Record<Tipo, { averbar: string; reservar: string }> = {
  EMPRESTIMO: { averbar: "Averbar Empréstimo", reservar: "Reservar Empréstimo" },
  REFIN: { averbar: "Averbar Refinanciamento", reservar: "Reservar Refinanciamento" },
  COMPOSTA: { averbar: "Averbar Composta", reservar: "Reservar Composta" },
};

export function OperacaoForm({ modo, tipo }: Props) {
  // `/contratos/averbar|reservar/:tipo` exige `carteira` na API. Sem ela — o
  // operador "Consulta de margem", por exemplo — a URL digitada a mao nao abre o
  // formulario: ele so' receberia 403 depois de preencher tudo.
  if (!bancoPermissaoCobre(readBancoPermissoesDaSessao(), "carteira")) {
    return <SemPermissaoOperacao modo={modo} />;
  }
  return <OperacaoFormDisponivel modo={modo} tipo={tipo} />;
}

function SemPermissaoOperacao({ modo }: { modo: Modo }) {
  const { idMatricula = "" } = useParams<{ idMatricula: string }>();
  const nav = useNavigate();
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, maxWidth: 640 }}>
      <h1 style={{ margin: 0, fontSize: "1.4rem" }}>Sem permissão</h1>
      <p style={{ margin: 0, color: "var(--text-muted)", fontSize: 14 }}>
        Este operador não pode {modo === "averbar" ? "averbar" : "reservar"} contratos. O acesso dele à margem é
        somente consulta.
      </p>
      <div>
        <Button variant="ghost" type="button" onClick={() => nav(`/banco/margem-contratacao/${idMatricula}`)}>
          Voltar para a ficha
        </Button>
      </div>
    </div>
  );
}

function OperacaoFormDisponivel({ modo, tipo }: Props) {
  const { idMatricula = "" } = useParams<{ idMatricula: string }>();
  const [sp] = useSearchParams();
  const idConvenioParam = sp.get("convenio") ?? undefined;
  const nav = useNavigate();

  const ficha = useQuery({
    queryKey: ["banco", "margem", idMatricula, idConvenioParam ?? null],
    queryFn: () => atlas.banco.margemBuscar({
      matricula: idMatricula.replace(/^MAT-/, ""),
      idConvenio: idConvenioParam,
    }),
    enabled: !!idMatricula,
  });

  // Condicoes INFORMADAS pelo banco (30/09/2026): financiado, liquido, parcela,
  // IOF, CET e numero do contrato saem do sistema do banco e sao digitados aqui.
  // Nada e' pre-preenchido por conta — a Atlas confere margem, teto de CET do
  // orgao e faixa de prazo, e so' AVISA quando a parcela nao condiz com o CET.
  const [valor, setValor] = useState<number | null>(null);
  const [valorParcela, setValorParcela] = useState<number | null>(null);
  const [iof, setIof] = useState<number | null>(null);
  const [parcelasTxt, setParcelasTxt] = useState<string>("");
  const [cetTxt, setCetTxt] = useState<string>("");
  const [carenciaTxt, setCarenciaTxt] = useState<string>("");
  const [jurosTxt, setJurosTxt] = useState<string>("");
  const [observacoes, setObservacoes] = useState<string>("");
  const [numeroContratoBanco, setNumeroContratoBanco] = useState<string>("");
  const [vencimento1aParcela, setVencimento1aParcela] = useState<string>("");
  const [dataLiberacaoCredito, setDataLiberacaoCredito] = useState<string>("");
  const [valorLiquido, setValorLiquido] = useState<number | null>(null);
  const [valorCarencia, setValorCarencia] = useState<number | null>(null);
  const [contratoOrigem, setContratoOrigem] = useState<string>("");
  const [bancoOrigem, setBancoOrigem] = useState<string>("");
  const [saldoOrigem, setSaldoOrigem] = useState<number | null>(null);
  const [valorRefin, setValorRefin] = useState<number | null>(null);

  const num = (s: string): number | null => {
    const t = s.trim().replace(",", ".");
    if (!t) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };
  const parcelas = num(parcelasTxt);
  const cetPct = num(cetTxt);
  const jurosPct = num(jurosTxt);
  const carenciaInformada = num(carenciaTxt);
  const carencia = carenciaInformada ?? 0;
  const taxaAm = cetPct != null ? Number((cetPct / 100).toFixed(6)) : null;

  /** CET anual equivalente, so' para conferencia. */
  const cetAa = taxaAm != null && taxaAm > 0 ? cetAnual(taxaAm) : null;

  /** Folha do 1o desconto = mes/ano do vencimento da 1a parcela (MM/AAAA).
   *  Somente leitura — deriva do campo acima, como na tela de referencia. */
  const folha1oDesconto = useMemo(() => {
    const m = /^(\d{4})-(\d{2})-\d{2}$/.exec(vencimento1aParcela);
    return m ? `${m[2]}/${m[1]}` : "—";
  }, [vencimento1aParcela]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!valor || valorParcela == null || parcelas == null || taxaAm == null) throw new Error("Preencha os campos obrigatórios");
      const body: NovoContratoBody = {
        idMatricula,
        valor,
        parcelas,
        taxaAm,
        jurosAm: jurosPct != null && jurosPct > 0 ? Number((jurosPct / 100).toFixed(6)) : undefined,
        valorParcela,
        iof: iof ?? 0,
        numeroContratoBanco: numeroContratoBanco.trim(),
        diasCarencia: carencia,
        observacoes: observacoes || undefined,
        contratoOrigem: contratoOrigem || undefined,
        bancoOrigem: bancoOrigem || undefined,
        saldoDevedorOrigem: saldoOrigem ?? undefined,
        valorRefin: valorRefin ?? undefined,
        vencimento1aParcela: vencimento1aParcela || undefined,
        dataLiberacaoCredito: dataLiberacaoCredito || undefined,
        valorLiquidoLiberado: valorLiquido ?? undefined,
        valorCarencia: valorCarencia ?? undefined,
      };
      return modo === "averbar" ? atlas.banco.averbar(tipo, body) : atlas.banco.reservar(tipo, body);
    },
    onSuccess: () => {
      nav("/banco/carteira");
    },
  });

  if (ficha.isLoading) return <div style={{ color: "var(--text-muted)" }}>Carregando...</div>;
  if (!ficha.data?.ficha) return <div style={{ color: "var(--danger-500)" }}>Servidor não encontrado.</div>;
  const f = ficha.data.ficha;

  const exigeOrigem = tipo === "REFIN" || tipo === "COMPOSTA";
  const fmtBRLUi = (v: number) => v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const pctBr = (v: number) => v.toFixed(2).replace(".", ",");
  const margemEmprestimo = f.margens.find((m) => m.tipo === "EMPRESTIMO")?.disponivel ?? f.margemDisponivel;
  const prazoMin = f.limitesConvenio?.prazoMin ?? 1;
  const prazoMax = f.limitesConvenio?.prazoMax ?? Math.min(f.limitesConvenio?.maxParcelas ?? 120, 120);
  const tetoCetPct = f.limitesConvenio?.taxaMaxAm ?? null;
  // Travas — as mesmas do backend.
  const excedeMargem = valorParcela != null && valorParcela > margemEmprestimo + 0.01;
  const excedeCet = tetoCetPct != null && cetPct != null && cetPct > tetoCetPct + 1e-9;
  const cetAbaixoMinimo = taxaAm != null && taxaAm > 0 && taxaAm < CET_MINIMO_AM - 1e-9;
  const cetAbaixoJuros = jurosPct != null && jurosPct > 0 && cetPct != null && cetPct < jurosPct - 1e-9;
  const prazoFora = parcelas != null && (parcelas < prazoMin || parcelas > prazoMax || !Number.isInteger(parcelas));
  const liquidoAcimaDoFinanciado = valor != null && valorLiquido != null && valorLiquido > valor + 0.005;
  // Aviso — nao trava.
  const avisoCoerencia = valorParcela != null && parcelas != null && taxaAm != null
    ? avisoCoerenciaParcela({ parcela: valorParcela, valorLiquido: valorLiquido ?? valor ?? 0, parcelas, cetAm: taxaAm, diasCarencia: carencia })
    : null;
  const totalPagar = valorParcela != null && parcelas != null ? Math.round(valorParcela * parcelas * 100) / 100 : null;
  const faltaObrigatorio =
    !valor || valor <= 0 || valorParcela == null || valorParcela <= 0 || parcelas == null || parcelas <= 0 ||
    taxaAm == null || taxaAm <= 0 || !numeroContratoBanco.trim() ||
    iof == null || carenciaInformada == null || carenciaInformada < 0;
  const bloqueio =
    excedeCet ? `CET de ${pctBr(cetPct ?? 0)}% a.m. excede o teto de ${pctBr(tetoCetPct ?? 0)}% do órgão.`
    : cetAbaixoMinimo ? `CET abaixo do mínimo de ${pctBr(CET_MINIMO_AM * 100)}% a.m. — o CET é informado em percentual (1,79 para 1,79%).`
    : cetAbaixoJuros ? "O CET não pode ficar abaixo dos juros — ele os inclui. Confira se a taxa de juros do seu sistema não foi digitada no lugar do CET (o CET está na CCB)."
    : liquidoAcimaDoFinanciado ? "O valor líquido liberado não pode ser maior que o valor financiado."
    : prazoFora ? `O prazo tem de ficar entre ${prazoMin}× e ${prazoMax}× (suas tabelas no convênio, limitadas pelo teto do órgão).`
    : excedeMargem && valorParcela != null ? `A parcela de ${fmtBRLUi(valorParcela)} excede a margem disponível (${fmtBRLUi(margemEmprestimo)}).`
    : null;

  return (
    <form
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        mutation.mutate();
      }}
      style={{ display: "flex", flexDirection: "column", gap: 20, maxWidth: 960 }}
    >
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          {modo === "averbar" ? "Averbação direta" : "Reserva pré-contrato"}
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>{TITULOS[tipo][modo]}</h1>
        <p style={{ color: "var(--text-muted)", marginTop: 4 }}>
          Servidor: <b>{f.nome}</b> — Matrícula {f.matricula} — {f.origem}
        </p>
        {/* Mesmo destaque da ficha de averbacao (cliente 05/08/2026): margem,
            parcela e total a pagar sempre visiveis, sem precisar procurar. */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
          <div style={{ minWidth: 160, padding: "10px 14px", borderRadius: 10, background: "var(--bg-elev-2)", border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-dim)" }}>
              Margem disponível
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, color: "var(--emerald-500)" }}>
              {fmtBRLUi(margemEmprestimo)}
            </div>
          </div>
          <div style={{ minWidth: 160, padding: "10px 14px", borderRadius: 10, background: excedeMargem ? "color-mix(in srgb, var(--danger-500) 12%, transparent)" : "var(--bg-elev-2)", border: `1px solid ${excedeMargem ? "var(--danger-500)" : "var(--border)"}` }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-dim)" }}>
              Valor da parcela
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, color: excedeMargem ? "var(--danger-500)" : "var(--text)" }}>
              {valorParcela ? fmtBRLUi(valorParcela) : "—"}
            </div>
          </div>
          <div style={{ minWidth: 160, padding: "10px 14px", borderRadius: 10, background: "var(--bg-elev-2)", border: "1px solid var(--border)" }}>
            <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-dim)" }}>
              Total a pagar
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 2, color: "var(--text)" }}>
              {totalPagar ? fmtBRLUi(totalPagar) : "—"}
            </div>
            <div style={{ fontSize: 10, color: "var(--text-dim)", marginTop: 2 }}>
              {totalPagar && valorParcela ? `${parcelas}× de ${fmtBRLUi(valorParcela)}` : " "}
            </div>
          </div>
        </div>
      </header>

      <section
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 14, color: "var(--text-muted)" }}>Dados da operação</h3>
        <FormGrid cols={3}>
          <CurrencyField
            label="Valor financiado"
            value={valor}
            onValueChange={setValor}
            required
          />
          <NumberField
            label={`Parcelas (${prazoMin}× a ${prazoMax}×)`}
            value={parcelasTxt}
            onChange={(e) => setParcelasTxt(e.target.value)}
            min={prazoMin}
            max={prazoMax}
            required
            error={prazoFora ? `Entre ${prazoMin}× e ${prazoMax}×` : undefined}
          />
          <CurrencyField
            label="Valor da parcela"
            value={valorParcela}
            onValueChange={setValorParcela}
            required
            hint="Calculada no seu sistema"
            error={excedeMargem ? "Acima da margem disponível" : undefined}
          />
          {/* A tela do banco mostra JUROS; o CET esta na CCB. Campos separados
              para os juros nao acabarem digitados no lugar do CET. */}
          <NumberField
            label="Juros mensal (%) — opcional"
            step={0.01}
            value={jurosTxt}
            onChange={(e) => setJurosTxt(e.target.value)}
            hint="A taxa de juros do seu sistema. Sem teto; o CET não pode ficar abaixo dela."
          />
          <NumberField
            label="CET (% a.m.) — conforme a CCB"
            step={0.01}
            value={cetTxt}
            onChange={(e) => setCetTxt(e.target.value)}
            required
            max={tetoCetPct ?? undefined}
            hint={`Custo Efetivo Total: juros + IOF + tarifas + seguro. Não é a taxa de juros.${tetoCetPct != null ? ` Teto do órgão: ${pctBr(tetoCetPct)}% a.m.` : ""}${cetAa != null ? ` Equivale a ${pctBr(cetAa * 100)}% a.a.` : ""}`}
            error={excedeCet ? "Acima do teto do órgão" : cetAbaixoJuros ? "Menor que os juros" : undefined}
          />
          <CurrencyField
            label="Valor IOF"
            value={iof}
            onValueChange={setIof}
            required
          />
          <NumberField
            label="Dias de carência"
            value={carenciaTxt}
            onChange={(e) => setCarenciaTxt(e.target.value)}
            min={0}
            max={180}
            required
            hint="Dias entre a liberação e a 1ª parcela"
          />
          <TextField
            label="Tipo de contrato"
            value={tipo}
            readOnly
          />
        </FormGrid>
      </section>

      {/* Dados do contrato no sistema do BANCO. O numero voltou a ser digitado
          pelo banco em 30/09/2026 e e' obrigatorio. */}
      <section
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 14, color: "var(--text-muted)" }}>Dados do contrato no seu sistema</h3>
        <FormGrid cols={3}>
          <TextField
            label="Número do contrato no seu sistema"
            value={numeroContratoBanco}
            onChange={(e) => setNumeroContratoBanco(e.target.value)}
            maxLength={60}
            required
            hint="O número do contrato no seu sistema — não pode repetir"
          />
          <TextField
            label="Vencimento 1ª parcela"
            type="date"
            value={vencimento1aParcela}
            onChange={(e) => setVencimento1aParcela(e.target.value)}
          />
          <TextField
            label="Folha 1º desconto"
            value={folha1oDesconto}
            readOnly
            hint="Derivado do vencimento da 1ª parcela"
          />
          <TextField
            label="Data de liberação do crédito"
            type="date"
            value={dataLiberacaoCredito}
            onChange={(e) => setDataLiberacaoCredito(e.target.value)}
          />
          <CurrencyField
            label="Valor líquido liberado"
            value={valorLiquido}
            onValueChange={setValorLiquido}
            hint="O que cai na conta do servidor"
            error={liquidoAcimaDoFinanciado ? "Maior que o financiado" : undefined}
          />
          <CurrencyField
            label="Valor carência"
            value={valorCarencia}
            onValueChange={setValorCarencia}
          />
        </FormGrid>
      </section>

      {bloqueio ? (
        <div style={{ padding: 10, borderRadius: 8, background: "color-mix(in srgb, var(--danger-500) 10%, transparent)", border: "1px solid var(--danger-500)", fontSize: 12, color: "var(--text)", lineHeight: 1.5 }}>
          {bloqueio}
        </div>
      ) : null}
      {avisoCoerencia && !bloqueio ? (
        <div style={{ padding: 10, borderRadius: 8, background: "color-mix(in srgb, var(--gold-500) 10%, transparent)", border: "1px solid var(--gold-500)", fontSize: 12, color: "var(--text)", lineHeight: 1.5 }}>
          A parcela informada difere em <b>{(avisoCoerencia.diferenca * 100).toFixed(1).replace(".", ",")}%</b> da parcela pelo CET
          informado (<b>{fmtBRLUi(avisoCoerencia.parcelaPeloCet)}</b>). Confira os números — a operação não é bloqueada por isso.
        </div>
      ) : null}

      {exigeOrigem ? (
        <section
          style={{
            background: "var(--bg-elev)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: 20,
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 14, color: "var(--text-muted)" }}>
            Origem do refinanciamento
          </h3>
          <FormGrid cols={3}>
            <TextField
              label="Credora"
              value={bancoOrigem}
              onChange={(e) => setBancoOrigem(e.target.value)}
              required
            />
            <TextField
              label="Contrato/ADF de origem"
              value={contratoOrigem}
              onChange={(e) => setContratoOrigem(e.target.value)}
              required
            />
            <CurrencyField label="Saldo devedor" value={saldoOrigem} onValueChange={setSaldoOrigem} required />
            {tipo === "COMPOSTA" ? (
              <CurrencyField label="Valor refin" value={valorRefin} onValueChange={setValorRefin} />
            ) : null}
          </FormGrid>
        </section>
      ) : null}

      <section
        style={{
          background: "var(--bg-elev)",
          border: "1px solid var(--border)",
          borderRadius: 12,
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 14, color: "var(--text-muted)" }}>Observações</h3>
        <TextareaField
          label="Observações internas"
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          hint="Texto livre para auditoria. Não aparece para o servidor."
        />
      </section>

      {mutation.error ? (
        <div style={{ color: "var(--danger-500)", fontSize: 13 }}>
          {mutation.error instanceof Error ? mutation.error.message : "Erro ao enviar"}
        </div>
      ) : null}

      <FormActions>
        <Button variant="ghost" type="button" onClick={() => nav(`/banco/margem-contratacao/${idMatricula}`)}>
          Cancelar
        </Button>
        {/* Mesmas travas do backend: obrigatorios, margem, teto de CET, prazo. */}
        <Button type="submit" disabled={mutation.isPending || faltaObrigatorio || bloqueio != null}>
          {mutation.isPending ? "Enviando..." : modo === "averbar" ? "Averbar →" : "Criar reserva →"}
        </Button>
      </FormActions>
    </form>
  );
}
